/**
 * Service for managing puzzle history in localStorage
 */

export interface PuzzleHistory {
    date: string; // ISO date string (YYYY-MM-DD)
    puzzle: {
        title: string;
        fen: string;
        pgn: string;
        moves: string[];
        playerColor: "w" | "b";
    };
    completed: boolean;
    attempts: number;
    hintsUsed: number;
    completedAt?: string; // ISO timestamp
}

const PUZZLE_HISTORY_KEY = "chess-puzzle-history";

/**
 * Cleans move notation by removing leading dots
 */
function cleanMoveSAN(move: string): string {
    return move.replace(/^\.+/, "");
}

/**
 * Gets all puzzle history from localStorage
 */
export function getPuzzleHistory(): PuzzleHistory[] {
    try {
        const history = localStorage.getItem(PUZZLE_HISTORY_KEY);
        if (!history) return [];

        const parsedHistory: PuzzleHistory[] = JSON.parse(history);

        // Clean move notation in case old data has leading dots
        return parsedHistory.map((entry) => ({
            ...entry,
            puzzle: {
                ...entry.puzzle,
                moves: entry.puzzle.moves.map(cleanMoveSAN),
            },
        }));
    } catch (error) {
        console.error("Error reading puzzle history:", error);
        return [];
    }
}

/**
 * Saves a puzzle attempt to history
 */
export function savePuzzleAttempt(
    date: string,
    puzzle: {
        title: string;
        fen: string;
        pgn: string;
        moves: string[];
        playerColor: "w" | "b";
    },
    completed: boolean = false,
    hintsUsed: number = 0,
    incrementAttempts: boolean = true, // Only increment when actually attempting the puzzle
): void {
    try {
        const history = getPuzzleHistory();
        const existingIndex = history.findIndex((entry) => entry.date === date);

        if (existingIndex >= 0) {
            // Update existing entry
            const existing = history[existingIndex];
            history[existingIndex] = {
                ...existing,
                attempts: incrementAttempts ? existing.attempts + 1 : existing.attempts,
                completed: completed || existing.completed,
                hintsUsed: existing.hintsUsed + hintsUsed,
                completedAt: completed ? new Date().toISOString() : existing.completedAt,
            };
        } else {
            // Create new entry
            history.push({
                date,
                puzzle,
                completed,
                attempts: incrementAttempts ? 1 : 0,
                hintsUsed,
                completedAt: completed ? new Date().toISOString() : undefined,
            });
        }

        localStorage.setItem(PUZZLE_HISTORY_KEY, JSON.stringify(history));
    } catch (error) {
        console.error("Error saving puzzle attempt:", error);
    }
}

/**
 * Gets puzzle for a specific date
 */
export function getPuzzleByDate(date: string): PuzzleHistory | null {
    const history = getPuzzleHistory();
    return history.find((entry) => entry.date === date) || null;
}

/**
 * Gets today's date in YYYY-MM-DD format
 */
export function getTodayDateString(): string {
    return new Date().toISOString().split("T")[0];
}

/**
 * Gets last N days of dates in YYYY-MM-DD format
 */
export function getLastNDays(n: number): string[] {
    const dates: string[] = [];
    const today = new Date();

    for (let i = 0; i < n; i++) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        dates.push(date.toISOString().split("T")[0]);
    }

    return dates;
}

/**
 * Checks if a puzzle exists for a given date
 */
export function hasPuzzleForDate(date: string): boolean {
    const history = getPuzzleHistory();
    return history.some((entry) => entry.date === date);
}

/**
 * Gets completion status for a date
 */
export function getPuzzleStatus(date: string): "completed" | "attempted" | "not-played" {
    const puzzle = getPuzzleByDate(date);
    if (!puzzle) return "not-played";
    if (puzzle.completed) return "completed";
    if (puzzle.attempts > 0) return "attempted";
    return "not-played";
}

/**
 * Clears all puzzle history (for testing/reset)
 */
export function clearPuzzleHistory(): void {
    localStorage.removeItem(PUZZLE_HISTORY_KEY);
}
